/**
 * Vista previa de la distribucion de un pago a cuenta de cliente.
 * DEBE coincidir con core/servidor/util/aplicacion_pago.js (el servidor es la fuente de verdad y
 * responde 409 si la distribucion que vio el usuario difiere de la que calcula con los balances reales).
 *
 * - Montos dirigidos: el usuario fija cuanto va a facturas concretas.
 * - El resto se reparte de la factura mas vieja a la mas nueva, sin tocar las dirigidas.
 */
import { roundTo2 } from './monetario';

function ordenarMasViejaPrimero(facturas) {
  return [...facturas].sort((a, b) => {
    const diff = new Date(a.fecha_hora_creacion) - new Date(b.fecha_hora_creacion);
    return diff !== 0 ? diff : a.id_factura - b.id_factura;
  });
}

/**
 * @param facturas facturas elegibles [{ id_factura, balance, fecha_hora_creacion }]
 * @param monto monto total del pago
 * @param dirigidos objeto { [id_factura]: monto escrito por el usuario } (vacios se ignoran)
 * @returns {{ aplicaciones, total_dirigido, restante, error }} error es un texto o null
 */
export function calcularDistribucion(facturas, monto, dirigidos = {}) {
  const total = roundTo2(monto);
  const porId = new Map(facturas.map(f => [f.id_factura, f]));
  const fijos = new Map();

  for (const [idTexto, valor] of Object.entries(dirigidos)) {
    if (valor === '' || valor === null || valor === undefined) continue;
    const id = Number(idTexto);
    const montoDirigido = roundTo2(valor);
    const factura = porId.get(id);
    if (!factura) continue;
    if (!(montoDirigido > 0)) return { aplicaciones: [], total_dirigido: 0, restante: 0, error: 'Los montos específicos deben ser mayores a 0', facturaConError: id };
    if (montoDirigido > roundTo2(factura.balance)) {
      return { aplicaciones: [], total_dirigido: 0, restante: 0, error: 'El monto específico excede el balance de la factura', facturaConError: id };
    }
    fijos.set(id, montoDirigido);
  }

  const totalDirigido = roundTo2([...fijos.values()].reduce((s, m) => s + m, 0));
  if (!(total > 0)) return { aplicaciones: [], total_dirigido: totalDirigido, restante: 0, error: null };
  if (totalDirigido > total) {
    return { aplicaciones: [], total_dirigido: totalDirigido, restante: 0, error: 'Los montos específicos superan el monto del pago' };
  }

  let restante = roundTo2(total - totalDirigido);
  const aplicaciones = [];
  for (const f of ordenarMasViejaPrimero(facturas)) {
    const balance = roundTo2(f.balance);
    let aplicado = 0;
    let origen = 'automatico';
    if (fijos.has(f.id_factura)) {
      aplicado = fijos.get(f.id_factura);
      origen = 'dirigido';
    } else if (restante > 0 && balance > 0) {
      aplicado = Math.min(restante, balance);
      restante = roundTo2(restante - aplicado);
    }
    if (aplicado > 0) {
      aplicaciones.push({ ...f, origen, monto_aplicado: aplicado, balance_posterior: roundTo2(balance - aplicado) });
    }
  }

  const error = restante > 0 ? 'El monto restante excede el balance disponible de las demás facturas' : null;
  return { aplicaciones, total_dirigido: totalDirigido, restante, error };
}
